/** Aplica cor da marca (primary_color / brand_accent) nos tokens CSS. */

import { ACCENT_PRESETS } from './themeAccents';

const VALID_ACCENT_KEYS = new Set(ACCENT_PRESETS.map((p) => p.key));

const INLINE_BRAND_PROPS = [
  '--primary-brand-color',
  '--brand',
  '--primary',
  '--primary-ink',
  '--primary-foreground',
  '--accent',
  '--accent-foreground',
  '--ring',
  '--brand-glow-rgb',
  '--primary-solid',
  '--primary-solid-foreground',
];

export function normalizeHexColor(input) {
  if (!input || typeof input !== 'string') return null;
  let s = input.trim();
  if (!s.startsWith('#')) s = `#${s}`;
  if (s.length === 4 && /^#([0-9a-f]{3})$/i.test(s)) {
    s = `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
  }
  if (!/^#[0-9a-f]{6}$/i.test(s)) return null;
  return s.toLowerCase();
}

export function hexToHslTriplet(hex) {
  const h = normalizeHexColor(hex);
  if (!h) return null;
  const r = parseInt(h.slice(1, 3), 16) / 255;
  const g = parseInt(h.slice(3, 5), 16) / 255;
  const b = parseInt(h.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let hue = 0;
  let sat = 0;
  if (max !== min) {
    const d = max - min;
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        hue = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        hue = ((b - r) / d + 2) / 6;
        break;
      default:
        hue = ((r - g) / d + 4) / 6;
        break;
    }
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 1000) / 10}% ${Math.round(l * 1000) / 10}%`;
}

export function clearInlineBrandPrimary() {
  const root = document.documentElement;
  INLINE_BRAND_PROPS.forEach((prop) => root.style.removeProperty(prop));
}

export function resolveAccentHex(accentKey, theme = 'dark') {
  const preset = ACCENT_PRESETS.find((p) => p.key === accentKey);
  if (!preset) return null;
  return theme === 'light' ? preset.swatchLight : preset.swatchDark;
}

export function hexToRgbTriplet(hex) {
  const h = normalizeHexColor(hex);
  if (!h) return null;
  return `${parseInt(h.slice(1, 3), 16)}, ${parseInt(h.slice(3, 5), 16)}, ${parseInt(h.slice(5, 7), 16)}`;
}

/** Hover/UI soft: --accent NÃO deve ser a primary cheia (senão hover:bg-accent fica ilegível). */
function isDarkThemeRoot() {
  try {
    const root = document.documentElement;
    return root.classList.contains('dark') || root.getAttribute('data-theme') === 'dark';
  } catch {
    return false;
  }
}

export function primaryTextColor(hex) {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return '#ffffff';
  const luminance = (value) => [1, 3, 5].map(i => parseInt(value.slice(i, i + 2), 16) / 255)
    .map(v => v <= .04045 ? v / 12.92 : ((v + .055) / 1.055) ** 2.4)
    .reduce((sum, v, i) => sum + v * [.2126, .7152, .0722][i], 0);
  const bg = luminance(normalized), ink = luminance('#0f172a');
  const lightContrast = 1.05 / (bg + .05);
  const darkContrast = (Math.max(bg, ink) + .05) / (Math.min(bg, ink) + .05);
  if (Math.max(lightContrast, darkContrast) < 4.5) return '#000000';
  return lightContrast >= darkContrast ? '#ffffff' : '#0f172a';
}

function softAccentFromPrimaryHsl(hslTriplet, theme) {
  const m = String(hslTriplet || '').match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/);
  if (!m) return null;
  const h = m[1];
  const s = Math.min(Number(m[2]) || 0, 72);
  const dark = theme === 'dark';
  return {
    accent: dark ? `${h} ${Math.min(s, 36)}% 18%` : `${h} ${Math.min(s, 55)}% 95%`,
    accentForeground: dark ? `${h} ${Math.max(s, 40)}% 78%` : `${h} ${Math.max(s, 45)}% 32%`,
  };
}

function applySoftAccentFromPrimary(root, primaryHsl, theme) {
  const soft = softAccentFromPrimaryHsl(primaryHsl, theme);
  if (!soft) return;
  root.style.setProperty('--accent', soft.accent);
  root.style.setProperty('--accent-foreground', soft.accentForeground);
}

export function applyBrandPrimaryFromHex(primaryHex, theme = isDarkThemeRoot() ? 'dark' : 'light') {
  const hex = normalizeHexColor(primaryHex);
  if (!hex) return;
  const hsl = hexToHslTriplet(hex);
  if (!hsl) return;
  const rgb = hexToRgbTriplet(hex);
  const root = document.documentElement;
  root.style.setProperty('--primary-brand-color', hex);
  root.style.setProperty('--brand', hex);
  root.style.setProperty('--primary', hsl);
  root.style.setProperty('--primary-ink', hsl);
  root.style.setProperty('--primary-foreground', hexToHslTriplet(primaryTextColor(hex)));
  root.style.setProperty('--primary-solid', hsl);
  root.style.setProperty('--primary-solid-foreground', hexToHslTriplet(primaryTextColor(hex)));
  applySoftAccentFromPrimary(root, hsl, theme);
  root.style.setProperty('--ring', hsl);
  // --success fica verde de status (não segue a cor da marca)
  root.style.removeProperty('--success');
  if (rgb) root.style.setProperty('--brand-glow-rgb', rgb);
  root.removeAttribute('data-accent');
}

export function applyAccentPreset(accentKey, theme = isDarkThemeRoot() ? 'dark' : 'light') {
  const hex = resolveAccentHex(accentKey, theme);
  if (!hex) return;
  applyBrandPrimaryFromHex(hex, theme);
  const root = document.documentElement;
  const preset = ACCENT_PRESETS.find(p => p.key === accentKey);
  const solid = preset.swatchLight;
  // Vivid orange needs dark labels. Preserve the fill rather than turning it
  // brown to accommodate white text; other presets retain their white labels.
  const foreground = hexToHslTriplet(primaryTextColor(solid));
  root.style.setProperty('--primary-foreground', foreground);
  root.style.setProperty('--primary-solid-foreground', foreground);
  root.style.setProperty('--primary-solid', hexToHslTriplet(solid));
  const ink = hexToHslTriplet(theme === 'light' ? preset.textLight || hex : hex);
  root.style.setProperty('--primary-ink', ink);
  root.style.setProperty('--ring', ink);
  rootSetAccent(accentKey);
}

function rootSetAccent(accent) {
  try {
    document.documentElement.setAttribute('data-accent', accent);
  } catch (_) {
    /* ignore */
  }
}

/**
 * Aplica brand_accent (preset) ou primary_color do escritório.
 * `force: true` — páginas públicas (login/cadastro/checkout) ignoram accent pessoal
 * sem apagar a preferência do utilizador no dashboard.
 */
export function applyAppearanceColors(
  { brand_accent, primary_color } = {},
  theme = 'dark',
  { force = false } = {},
) {
  const accent = (brand_accent || '').trim();

  let userPinned = false;
  try {
    userPinned = window.localStorage.getItem('aurax-accent-user') === '1';
  } catch (_) {
    /* ignore */
  }

  if (userPinned && !force) {
    return;
  }

  if (accent === 'neutral') {
    clearInlineBrandPrimary();
    try {
      document.documentElement.removeAttribute('data-accent');
    } catch (_) {
      /* ignore */
    }
    return;
  }

  if (accent && VALID_ACCENT_KEYS.has(accent)) {
    applyAccentPreset(accent, theme);
    return;
  }

  if (primary_color) {
    applyBrandPrimaryFromHex(primary_color, theme);
    try {
      document.documentElement.removeAttribute('data-accent');
    } catch (_) {
      /* ignore */
    }
  }
}
